import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_AUTO_DISMISS_MS = 7000;
const DEFAULT_MAX_SIZE = 3;

/** @typedef {'success'|'info'|'error'} NotificationType */

/**
 * Simple stacking toast queue: push a message, cap visible items, auto-dismiss
 * after a delay, or dismiss by id.
 *
 * @param {{ autoDismissMs?: number, maxSize?: number }} [options]
 * @returns {{
 *   items: Array<{ id: string, message: string, type: NotificationType }>,
 *   push: (message: string, type?: NotificationType) => string,
 *   dismiss: (id: string) => void,
 * }}
 */
export function useNotificationQueue({
  autoDismissMs = DEFAULT_AUTO_DISMISS_MS,
  maxSize = DEFAULT_MAX_SIZE,
} = {}) {
  const [items, setItems] = useState([]);
  const timersRef = useRef(new Map());
  const visibleLimit =
    Number.isFinite(maxSize) && maxSize > 0
      ? Math.max(1, Math.floor(maxSize))
      : DEFAULT_MAX_SIZE;

  const dismiss = useCallback((id) => {
    const tid = timersRef.current.get(id);
    if (tid != null) {
      window.clearTimeout(tid);
    }
    timersRef.current.delete(id);
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (message, type = "info") => {
      const normalized =
        type === "success" || type === "error" ? type : "info";
      const id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setItems((prev) => {
        if (
          prev.some(
            (item) => item.message === message && item.type === normalized
          )
        ) {
          return prev;
        }
        const next = [...prev, { id, message, type: normalized }];
        while (next.length > visibleLimit) {
          const removed = next.shift();
          const removedTimer = timersRef.current.get(removed.id);
          if (removedTimer != null) {
            window.clearTimeout(removedTimer);
          }
          timersRef.current.delete(removed.id);
        }
        return next;
      });
      const tid = window.setTimeout(() => {
        timersRef.current.delete(id);
        dismiss(id);
      }, autoDismissMs);
      timersRef.current.set(id, tid);
      return id;
    },
    [autoDismissMs, dismiss, visibleLimit]
  );

  useEffect(
    () => () => {
      timersRef.current.forEach((tid) => window.clearTimeout(tid));
      timersRef.current.clear();
    },
    []
  );

  return { items, push, dismiss };
}
